import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Image as ImageIcon, FileText, Upload, Trash2, Loader2, Eye, Send, CheckCircle2, CalendarClock, History } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DOC_STATUSES } from "@/lib/roles";
import { fmtDate, fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

import { confirmAction } from "@/lib/dialogs";
export default function Engineering() {
  const { projectId, canWrite } = useProjectCtx();
  const canEdit = canWrite("ENGINEERING");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [updating, setUpdating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/engineering`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);
  const leafItems = items.filter((i) => !i.is_group);

  return (
    <div className="space-y-4" data-testid="engineering-page">
      <PageHeader
        title="Engineering — Drawing Status"
        description={canEdit ? "You can submit and approve drawings." : "Read-only for your project role."}
        action={canEdit && (
          <Button onClick={() => setUpdating(true)} data-testid="update-progress-button">
            <Send className="h-4 w-4" /> Update
          </Button>
        )}
      />
      <Tabs defaultValue="drawings" data-testid="engineering-tabs">
        <TabsList>
          <TabsTrigger value="drawings" data-testid="engineering-tab-drawings">Drawings</TabsTrigger>
          <TabsTrigger value="log" data-testid="engineering-tab-log"><History className="mr-1.5 h-3.5 w-3.5" /> Update Log</TabsTrigger>
        </TabsList>

        <TabsContent value="drawings" className="space-y-4">
          <div className="rounded-xl border border-border bg-card">
            {!loading && leafItems.length === 0 ? (
              <EmptyState title="No engineering items" description="Add drawings under WBS & Weightages first." testId="engineering-empty-state" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Code</TableHead><TableHead>Drawing</TableHead><TableHead>Discipline</TableHead><TableHead>Target</TableHead><TableHead>Status</TableHead><TableHead>Submitted</TableHead><TableHead>Approved</TableHead><TableHead className="text-right"></TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {leafItems.map((i) => (
                    <TableRow key={i.id} data-testid={`engineering-row-${i.id}`}>
                      <TableCell className="font-mono text-xs">{i.code}</TableCell>
                      <TableCell className="text-foreground">{i.name}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{i.engineering?.discipline || "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.engineering?.target_date)}</TableCell>
                      <TableCell><StatusPill value={i.engineering?.status} /></TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.engineering?.submitted_date)}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(i.engineering?.approved_date)}</TableCell>
                      <TableCell className="whitespace-nowrap text-right">
                        {i.attachments?.length > 0 && (
                          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground" title={`${i.attachments.length} file(s) attached`}>
                            <ImageIcon className="h-3 w-3" /> {i.attachments.length}
                          </span>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="ghost" onClick={() => setEditing(i)} data-testid={`edit-engineering-btn-${i.id}`} title="Full edit"><Pencil className="h-3.5 w-3.5" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="log">
          <UpdateLog projectId={projectId} />
        </TabsContent>
      </Tabs>
      {editing && <EngineeringDialog projectId={projectId} item={editing} canEdit={canEdit} onClose={() => setEditing(null)} onSaved={load} />}
      {updating && <QuickUpdateDialog projectId={projectId} items={leafItems} onClose={() => setUpdating(null)} onSaved={load} />}
    </div>
  );
}

function UpdateLog({ projectId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/progress-entries`, { params: { category: "ENGINEERING", limit: 100 } });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const METRIC_LABELS = { DOC_STATUS: "Status", STAGE_DATE: "Date" };

  return (
    <div className="rounded-xl border border-border bg-card" data-testid="engineering-update-log">
      {!loading && rows.length === 0 ? (
        <EmptyState title="No updates yet" description="Every Update you make will show up here, with who made it." testId="engineering-log-empty-state" />
      ) : (
        <Table>
          <TableHeader>
            <TableRow><TableHead>Date</TableHead><TableHead>Drawing</TableHead><TableHead>What changed</TableHead><TableHead>Before → After</TableHead><TableHead>Updated by</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const fieldLabel = r.sub_stage
                ? r.sub_stage.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())
                : METRIC_LABELS[r.metric] || r.metric.replaceAll("_", " ");
              const after = r.value_date ? fmtDate(r.value_date) : r.status_value ?? (r.quantity !== null ? fmtNum(r.quantity) : "—");
              const before = r.previous_value || "—";
              return (
                <TableRow key={r.id} data-testid={`engineering-log-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(r.entry_date)}</TableCell>
                  <TableCell className="text-xs">
                    {r.item_code ? <span className="font-mono text-foreground">{r.item_code}</span> : "—"}
                    {r.item_name && <div className="text-[10px] text-muted-foreground">{r.item_name}</div>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fieldLabel}</TableCell>
                  <TableCell className="font-mono text-xs">
                    <span className="text-muted-foreground">{before}</span> <span className="text-muted-foreground">→</span> <span className="font-semibold text-foreground">{after}</span>
                  </TableCell>
                  <TableCell className="text-xs">{r.entered_by_name || <span className="italic text-muted-foreground">Unknown</span>}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function QuickUpdateDialog({ projectId, items, onClose, onSaved }) {
  const [itemId, setItemId] = useState("");
  const [phase, setPhase] = useState("");
  const [entryDate, setEntryDate] = useState("");
  const [dateTouched, setDateTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const item = items.find((i) => i.id === itemId);

  const chooseItem = (v) => {
    setItemId(v);
    setPhase("");
    setAttachments(items.find((i) => i.id === v)?.attachments || []);
  };
  const choosePhase = (v) => {
    setPhase(v);
    if (!dateTouched) setEntryDate(todayISO());
  };
  const changeDate = (e) => {
    setEntryDate(e.target.value);
    setDateTouched(true);
  };

  const submit = async () => {
    if (!item || !phase || !entryDate) return;
    setSaving(true);
    const payload =
      phase === "TARGET" ? { target_date: entryDate, entry_date: entryDate }
      : phase === "SUBMITTED" ? { status: "SUBMITTED", submitted_date: entryDate, entry_date: entryDate }
      : { status: "APPROVED", approved_date: entryDate, entry_date: entryDate };
    try {
      await api.patch(`/projects/${projectId}/engineering/${item.id}`, payload);
      toast.success(phase === "TARGET" ? "Target date updated" : phase === "SUBMITTED" ? "Marked as submitted" : "Marked as approved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) { onSaved(); onClose(); } }}>
      <DialogContent className="sm:max-w-sm" data-testid="engineering-quick-update-dialog">
        <DialogHeader><DialogTitle>Update today's progress</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Drawing</Label>
            <Select value={itemId} onValueChange={chooseItem}>
              <SelectTrigger data-testid="quick-update-item-select" className="mt-1.5"><SelectValue placeholder="Choose a drawing" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {item && (
            <div>
              <Label>Phase</Label>
              <Select value={phase} onValueChange={choosePhase}>
                <SelectTrigger data-testid="quick-update-phase-select" className="mt-1.5"><SelectValue placeholder="Target date, Submit or Approval" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TARGET"><span className="inline-flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Target date</span></SelectItem>
                  <SelectItem value="SUBMITTED"><span className="inline-flex items-center gap-1.5"><Send className="h-3.5 w-3.5" /> Submit</span></SelectItem>
                  <SelectItem value="APPROVED"><span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5" /> Approval</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {item && phase && (
            <div>
              <Label>{phase === "TARGET" ? "Target date" : phase === "SUBMITTED" ? "Submitted date" : "Approved date"}</Label>
              <Input type="date" value={entryDate} onChange={changeDate} className="mt-1.5" data-testid="quick-update-date-input" />
              <p className="mt-1 text-[11px] text-muted-foreground">
                {phase === "TARGET" ? "Filled with today's date — change it to the planned date." : "Filled in automatically with today's date — change it if this happened on a different day."}
              </p>
            </div>
          )}
          {item && <AttachmentsPanel projectId={projectId} itemId={item.id} attachments={attachments} setAttachments={setAttachments} canEdit />}
        </div>
        <DialogFooter>
          {item && !phase && <p className="mr-auto self-center text-[11px] text-muted-foreground">Uploads save instantly — pick a phase too if you're also updating a date.</p>}
          <Button onClick={submit} disabled={!item || !phase || !entryDate || saving} data-testid="quick-update-submit-button">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttachmentsPanel({ projectId, itemId, attachments, setAttachments, canEdit }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [previews, setPreviews] = useState({});

  useEffect(() => {
    let cancelled = false;
    const urls = [];
    (async () => {
      for (const a of attachments) {
        if (previews[a.id]) continue;
        try {
          const res = await api.get(`/projects/${projectId}/engineering/${itemId}/attachments/${a.id}`, { responseType: "blob" });
          if (cancelled) return;
          const url = URL.createObjectURL(res.data);
          urls.push(url);
          setPreviews((p) => ({ ...p, [a.id]: { url, contentType: res.data.type } }));
        } catch {
          // ignore — the thumbnail just stays a placeholder
        }
      }
    })();
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, itemId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/engineering/${itemId}/attachments`, body, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments(data.attachments || []);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = async (attachmentId) => {
    if (!await confirmAction("Remove this file?")) return;
    try {
      const { data } = await api.delete(`/projects/${projectId}/engineering/${itemId}/attachments/${attachmentId}`);
      setAttachments(data.attachments || []);
      toast.success("File removed");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div>
      <Label>Design / drawing images</Label>
      <div className="mt-1.5 space-y-2">
        {attachments.length === 0 && <p className="text-xs text-muted-foreground">No files uploaded yet.</p>}
        {attachments.length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {attachments.map((a) => {
              const isImage = /\.(png|jpe?g|gif|webp)$/i.test(a.filename);
              const preview = previews[a.id];
              return (
                <div key={a.id} className="group relative overflow-hidden rounded-lg border border-border" data-testid={`attachment-thumb-${a.id}`}>
                  <button
                    type="button"
                    title={a.filename}
                    disabled={!preview}
                    onClick={() => preview && window.open(preview.url, "_blank", "noopener")}
                    className="block aspect-square w-full bg-muted"
                  >
                    {isImage && preview ? (
                      <img src={preview.url} alt={a.filename} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-muted-foreground">
                        {preview ? <FileText className="h-6 w-6" /> : <Loader2 className="h-5 w-5 animate-spin" />}
                        <span className="px-1 text-center text-[9px] leading-tight">{a.filename}</span>
                      </div>
                    )}
                  </button>
                  <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-black/60 px-1.5 py-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button type="button" disabled={!preview} onClick={() => preview && window.open(preview.url, "_blank", "noopener")} className="text-white" title="View"><Eye className="h-3.5 w-3.5" /></button>
                    {canEdit && (
                      <button type="button" onClick={() => removeAttachment(a.id)} className="text-white" title="Delete" data-testid={`delete-attachment-${a.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {canEdit && (
          <div>
            <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx" className="hidden" onChange={handleUpload} data-testid="eng-attachment-input" />
            <Button type="button" size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="eng-attachment-upload-button">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload image / file
            </Button>
            <p className="mt-1 text-[11px] text-muted-foreground">PDF, Word, Excel, JPG or PNG.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function EngineeringDialog({ projectId, item, canEdit, onClose, onSaved }) {
  const d = item.engineering || {};
  const [attachments, setAttachments] = useState(item.attachments || []);
  const [form, setForm] = useState({
    target_date: d.target_date || "", status: d.status || "NOT_STARTED", submitted_date: d.submitted_date || "",
    approved_date: d.approved_date || "", revision: d.revision || "", discipline: d.discipline || "",
    critical_for_construction: d.critical_for_construction || false, remarks: d.remarks || "", entry_date: todayISO(),
  });
  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const submit = async () => {
    try {
      await api.patch(`/projects/${projectId}/engineering/${item.id}`, {
        ...form,
        target_date: form.target_date || null, submitted_date: form.submitted_date || null, approved_date: form.approved_date || null,
      });
      toast.success("Engineering status updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="engineering-dialog">
        <DialogHeader><DialogTitle>{item.code} — {item.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Target date</Label><Input type="date" data-testid="eng-target-date-input" value={form.target_date} onChange={set("target_date")} className="mt-1.5" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="eng-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Submitted date</Label><Input type="date" data-testid="eng-submitted-date-input" value={form.submitted_date} onChange={set("submitted_date")} className="mt-1.5" /></div>
            <div><Label>Approved date</Label><Input type="date" data-testid="eng-approved-date-input" value={form.approved_date} onChange={set("approved_date")} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Revision</Label><Input data-testid="eng-revision-input" value={form.revision} onChange={set("revision")} className="mt-1.5" /></div>
            <div><Label>Discipline</Label><Input data-testid="eng-discipline-input" value={form.discipline} onChange={set("discipline")} className="mt-1.5" /></div>
          </div>
          <div className="flex items-center gap-2"><Switch checked={form.critical_for_construction} onCheckedChange={(v) => setForm({ ...form, critical_for_construction: v })} data-testid="eng-critical-switch" /><Label>Critical for construction</Label></div>
          <div><Label>Remarks</Label><Textarea data-testid="eng-remarks-input" value={form.remarks} onChange={set("remarks")} className="mt-1.5" /></div>
          <div><Label>Back-date this entry (optional)</Label><Input type="date" data-testid="eng-entry-date-input" value={form.entry_date} onChange={set("entry_date")} className="mt-1.5" /></div>
        </div>
          <AttachmentsPanel projectId={projectId} itemId={item.id} attachments={attachments} setAttachments={setAttachments} canEdit={canEdit} />
        <DialogFooter><Button onClick={submit} data-testid="engineering-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
